import React, { useEffect, useState, useRef, useContext } from "react";
import { getImages } from "../Api";
import { useNavigate, useLocation } from "react-router-dom";
import { SocketContext } from "../context/SocketProvider";
import { useImageSelection } from "../context/useImageSelection";
import { useSettings } from "../context/SettingsContext";
import { useRoleBackground } from "../hooks/useRoleBackground";
import { useLoading } from "../context/LoadingContext";
import { useLanguage } from "../context/LanguageContext";
import { FaChevronRight } from "react-icons/fa6";
import { DEFAULT_ROOM } from "../constants/gameStages";
import ImageEnlargeOverlay from "./ImageEnlargeOverlay";

/** * ImageSelection component allows the user to select an image for describing.
 * It fetches images, displays them in a grid, and allows the user to select one.
 */
const ImageSelection = () => {
  // Use context to get selectedIndex and animation state
  const location = useLocation();
  const { shouldRefetch = true, allImages } = location.state || {};
  const { selectedIndex, setSelectedIndex } = useImageSelection();
  const { imageCount } = useSettings();
  const { socket } = useContext(SocketContext);
  const [images, setImages] = useState([]);
  const { bgStyle, className } = useRoleBackground("pink");
  const { loading, setLoading } = useLoading(); // Use global loading state
  const { t } = useLanguage();

  // Remove mount effect that resets selectedIndex and animation state
  const [modalImage, setModalImage] = useState(null);
  const navigate = useNavigate();
  const [dotCount, setDotCount] = useState(1);

  // Modal close handler
  const handleCloseModal = () => setModalImage(null);

  // Ref to track for double fetching
  const hasFetched = useRef(false);

  
  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev === 3 ? 1 : prev + 1));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const dotString = ".".repeat(dotCount);

  // Handle image click: only allow navigation for selectedIndex, else show modal
  const handleImageClick = (index) => {
    if (index === selectedIndex) {
      if (images[index]) {
        const selectedImage = {
          ...images[index],
          index,
        };
        navigate("/describeimage", {
          state: {
            selectedImage,
            allImages: images,
            selectedIndex: index,
          },
        });
      }
    } else {
      setModalImage(images[index]);
    }
  };

  const handleContinue = () => {
    if (selectedIndex !== null) {
      navigate("/describeimage", {
        state: {
          selectedImage: {
            ...images[selectedIndex],
            index: selectedIndex,
          },
          allImages: images, // Pass entire set for later reference
          images: images, // Pass entire set for later reference
        },
      });
    }
  };

  // image fetching logic
  useEffect(() => {
    if (
      !shouldRefetch &&
      Array.isArray(allImages) &&
      allImages.length >= imageCount
    ) {
      setImages(allImages.slice(0, imageCount));
      setLoading(false);
      // Start the timer since images are already available
      socket.emit("start_timer", { room: DEFAULT_ROOM, duration: 90 });
      return;
    }
    if (images.length >= imageCount || hasFetched.current) return;
    setLoading(true);
    hasFetched.current = true;

    console.log("Fetching images once...");
    fetchImages(); // will set loading to false when done
  }, [shouldRefetch, allImages, imageCount, images.length, socket, setLoading]);

  const fetchImages = async () => {
    let canceled = false;

    try {
      const data = await getImages();
      if (
        data.success &&
        Array.isArray(data.result) &&
        data.result.length >= imageCount
      ) {
        if (!canceled) {
          setImages(data.result.slice(0, imageCount));
        }
      } else {
        // fallback to dummy
        if (!canceled) {
          setImages(
            Array.from({ length: imageCount }).map((_, i) => ({
              url: `https://dummyimage.com/300x300/000000/fff.jpg&text=${i}`,
            }))
          );
        }
      }
    } catch (e) {
      if (!canceled) {
        setImages(
          Array.from({ length: imageCount }).map((_, i) => ({
            url: `https://dummyimage.com/300x300/000000/fff.jpg&text=${i}`,
          }))
        );
      }
    } finally {
      if (!canceled) {
        setLoading(false);
        // Start the timer once images are loaded
        socket.emit("start_timer", { room: DEFAULT_ROOM, duration: 90 });
      }
    }
  };

  // Animation effect (runs when images are loaded and no selectedIndex is set)

  // Auto-select a random image when images are loaded
  useEffect(() => {
    if (images.length > 0 && selectedIndex === null) {
      const randomIndex = Math.floor(Math.random() * images.length);
      setSelectedIndex(randomIndex);
    }
  }, [images, selectedIndex, setSelectedIndex]);

  if (loading) {
    return (
      <div
        className={`min-h-screen flex flex-col justify-center items-center ${className}`}
        style={bgStyle}
      >
        <div className="text-white text-2xl">
          {t('describer.loading', 'Loading images')}
          <span className="inline-block w-12 text-left">{dotString}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`p-[64px] flex flex-col items-center ${className}`}
      style={bgStyle}
    >
      <div className="w-[1052px] bg-white rounded-lg shadow-lg text-center">
        <h2
          className={`bg-pink400 w-full rounded-t-lg font-bold text-white text-xl font-[22px] py-[16px] px-[12px]`}
        >
          {t('imageSelector.selectedImage', 'THE SELECTED IMAGE TO DESCRIBE:')}
        </h2>

        <div
          className={`grid ${
            imageCount === 6 ? "grid-cols-3" : "grid-cols-4"
          } pt-[34px] pb-[14px] px-[56px] gap-[12px] justify-items-center `}
        >
          {images.map((img, index) => (
            <img
              key={index}
              className={`size-[225px] rounded-[16px] cursor-pointer transition-all ${
                selectedIndex === index
                  ? "border-[6px] border-aicom_pink opacity-100 scale-105 shadow-lg"
                  : "opacity-50 hover:shadow-lg"
              }`}
              src={img.url}
              alt={`Grid item ${index + 1}`}
              onClick={() => handleImageClick(index)}
            />
          ))}
        </div>

        <div className="flex gap-[24px] mb-[24px] ml-[884px]">
          <button
            className={`btn-pink-sm rounded-[10px] relative`}
            onClick={handleContinue}
            disabled={selectedIndex === null}
          >
            <span className="btn-text-pos-right-sm">{t('button.next', 'Next')} </span>
            <FaChevronRight className="arrow-pos-right-sm" />
          </button>
        </div>
        
        {/* Image Enlargement Overlay */}
        <ImageEnlargeOverlay 
          image={modalImage} 
          onClose={handleCloseModal} 
        />
      </div>
    </div>
  );
};

export default ImageSelection;
